import React from 'react';
import Icon from '@/shared/components/Icon';
import clsx from 'clsx';

import {formatCatalogPrice, type CatalogPanelDisplay} from './catalogUtils';
import {CatalogTestDotRow} from './CatalogTestDotRow';

type SelectionVariant = 'none' | 'cascade' | 'manual';

interface CatalogPanelCardProps {
    panel: CatalogPanelDisplay;
    expanded?: boolean;
    onToggleExpand: () => void;
    /** Provider: show selection checkbox. */
    showCheckbox?: boolean;
    checked?: boolean;
    disabled?: boolean;
    onCheck?: () => void;
    /** Slot for a badge (CatalogBadge, etc.). */
    badge?: React.ReactNode;
    selectionVariant?: SelectionVariant;
    showPrice?: boolean;
    /** Admin: show AlertTriangle for $0 test prices inside expansion. */
    showAlertIcon?: boolean;
    /** Admin: slot for edit/delete buttons. */
    headerActions?: React.ReactNode;
}

const VARIANT_CLASSES: Record<SelectionVariant, string> = {
    none: 'border border-gray-200 bg-gray-50 hover:bg-gray-100',
    // "cascade" follows brand (positive/included); "manual" stays blue
    // as the intentional contrast for explicit human selection.
    cascade:
        'border border-mld-primary/20 border-l-[3px] border-l-mld-primary bg-mld-primary/5',
    manual: 'border border-mld-info/20 border-l-[3px] border-l-blue-500 bg-mld-info/5/50',
};

const CatalogPanelCard: React.FC<CatalogPanelCardProps> = ({
    panel,
    expanded = false,
    onToggleExpand,
    showCheckbox = false,
    checked = false,
    disabled = false,
    onCheck,
    badge,
    selectionVariant = 'none',
    showPrice = true,
    showAlertIcon = false,
    headerActions,
}) => (
    <div>
        <div
            className={clsx(
                'flex items-center gap-3 rounded-md px-3 py-2 transition-colors',
                VARIANT_CLASSES[selectionVariant],
            )}>
            {showCheckbox && (
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={onCheck}
                    disabled={disabled}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-mld-info focus:ring-blue-500"
                />
            )}
            <button
                type="button"
                onClick={onToggleExpand}
                className="flex flex-1 items-center justify-between text-left">
                <div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-800">{panel.name}</span>
                        {showAlertIcon && panel.tests.some((t) => !t.inAssayCatalog || t.isMissingPrerequisite) && (
                            <span title={panel.tests.some((t) => t.isMissingPrerequisite) ? 'Panel is missing prerequisite tests' : 'Some tests do not match the assay catalog'}>
                                <Icon name="triangle-exclamation" className="h-3.5 w-3.5 shrink-0 text-mld-danger" />
                            </span>
                        )}
                        {badge}
                    </div>
                    <div className="text-[10px] text-gray-400">
                        {panel.tests.length} test{panel.tests.length === 1 ? '' : 's'}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {showPrice && (
                        <span className="font-mono text-xs font-semibold text-mld-primary">
                            {formatCatalogPrice(panel.totalAddonPrice)}
                        </span>
                    )}
                    {expanded ? (
                        <Icon name="chevron-down" className="h-3.5 w-3.5 text-gray-400" />
                    ) : (
                        <Icon name="chevron-right" className="h-3.5 w-3.5 text-gray-400" />
                    )}
                </div>
            </button>
            {headerActions}
        </div>

        {expanded && (
            <div className="ml-6 mt-0.5 mb-1 space-y-0.5">
                {panel.tests.map((test) => (
                    <CatalogTestDotRow
                        key={test.id}
                        test={test}
                        includedInBundledPanel={!!panel.isBundledPanel}
                        showAlertIcon={showAlertIcon}
                    />
                ))}
            </div>
        )}
    </div>
);

export {CatalogPanelCard};
export type {SelectionVariant};
